import type React from "react";

/** Every theme id. Order here is the switcher's cycle order. */
export const THEME_IDS = [
  "alien",
  "matrix",
  "conjuring",
  "shining",
] as const;

export type ThemeId = (typeof THEME_IDS)[number];

export const isThemeId = (value: unknown): value is ThemeId =>
  typeof value === "string" && (THEME_IDS as readonly string[]).includes(value);

/**
 * The one card shape every section normalizes to. Experience, projects, and
 * publications all reduce to this, so a theme writes a single Card renderer
 * rather than three.
 */
export type CardItem = {
  title: string;
  /** Present when the title itself should link out. */
  href?: string;
  /** Role for experience; unused elsewhere. */
  subtitle?: string;
  /** Dates for experience, venue for publications. */
  meta?: string;
  description: string;
  /** Trailing call-to-action, e.g. projects' "View →". */
  action?: { label: string; href: string };
};

export type HeroProps = { name: string; tagline: string };
export type SectionHeaderProps = { title: string; index: number };
export type CardProps = { item: CardItem; index: number };

/**
 * A theme is metadata plus five slot renderers. Slots are the only place a
 * theme may diverge structurally — no component outside this registry branches
 * on a theme id.
 */
export type Theme = {
  id: ThemeId;
  /** Shown in the switcher. */
  label: string;
  /** Swatch color in the switcher; mirrors the theme's --color-accent. */
  accent: string;
  /** Display font, loaded only when this theme activates. */
  fontHref?: string;
  Hero: React.FC<HeroProps>;
  SectionHeader: React.FC<SectionHeaderProps>;
  Card: React.FC<CardProps>;
  /** Ambient layer behind content. */
  Backdrop: React.FC | null;
  /** Fixed overlay above content (scanlines, grain, vignette). */
  Chrome: React.FC | null;
};
